import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import Tag from "../../components/Tags";
import GroupApis from "../../api/groupApis";
import { toast } from "sonner";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import { getGroupDetails } from "../../features/group-houses/GroupSlice";

interface Props {
  groupId?: number;
  refreshKey?: number;
  selectedStudentIds: number[];
  setSelectedStudentIds: React.Dispatch<React.SetStateAction<number[]>>;
}

export default function AddedStudentList({
  groupId,
  refreshKey,
  selectedStudentIds,
  setSelectedStudentIds
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const { confirm, modal } = useConfirmToast();

  const { groupDetails } = useSelector((state: RootState) => state.group);

  const { students: allStudents } = useSelector(
    (state: RootState) => state.student,
  );

  const existingStudents = groupId ? groupDetails?.students || [] : [];

  // Get full student objects for selected IDs
  const selectedStudents = allStudents.filter((s) =>
    selectedStudentIds.includes(s.admissionNumber),
  );

  // Merge + remove duplicates
  const studentsMap = new Map<number, any>();

  [...existingStudents, ...selectedStudents].forEach((s) => {
    studentsMap.set(s.admissionNumber, s);
  });

  const students = Array.from(studentsMap.values());
  const hasStudents = students.length > 0;

  useEffect(() => {
    if (groupId) {
      dispatch(getGroupDetails(groupId));
    }
  }, [groupId, refreshKey]);

  const handleRemove = async (studentId: number) => {
    if (!groupId) return;

    const ifOk = await confirm(
      "Are you sure you want to remove this student from the group?",
    );
    if (!ifOk) return;

    try {
      await GroupApis.removeStudent(groupId, studentId);
      toast.success("Student removed");

      // Refresh group students
      dispatch(getGroupDetails(groupId));
    } catch {
      // toast handled in API
    }
  };

  return (
    <div className="bg-white px-6 py-5 rounded-lg flex flex-col w-full lg:max-w-xs gap-7">
      <h3 className="text-xl text-neutral-400 font-semibold">
        Added Students List
      </h3>

      {hasStudents ? (
        <div className="max-h-96 overflow-y-auto flex flex-col gap-5 custom-scrollbar">
          {students?.map((t) => (
            <div
              key={t.admissionNumber}
              className="flex justify-between items-center"
            >
              <div className="flex gap-5">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
                  {t.fullName.charAt(0).toUpperCase()}
                </div>
                <div className="font-medium mt-1 text-sm text-neutral-300">
                  {t.fullName}
                </div>
              </div>
              <Tag
                text="Remove"
                color="danger"
                onClick={() => handleRemove(t.admissionNumber)}
              />
            </div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center text-center gap-3 py-10">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
            👨‍🎓
          </div>
          <p className="text-sm font-medium text-neutral-400">
            No students added yet
          </p>
          <p className="text-xs text-neutral-300">
            Students you add will appear here
          </p>
        </div>
      )}
      {modal}
    </div>
  );
}
